/**
 * cart-redirection.js
 * Intercepts checkout and add-to-cart actions on zennuvo.com
 * and redirects the user to zennuvo.store with the mapped cart contents.
 */

(function () {
    const TARGET_STORE_DOMAIN = 'https://zennuvo.net';

    // Helper to get locale/market subpath (e.g. '/' or '/de-ch/')
    function getRootPath() {
        let root = '/';
        if (typeof window.theme !== 'undefined' && window.theme.routes && window.theme.routes.root_url) {
            root = window.theme.routes.root_url;
        } else if (typeof window.Shopify !== 'undefined' && window.Shopify.routes && window.Shopify.routes.root) {
            root = window.Shopify.routes.root;
        }

        // Ensure root path format is clean before appending
        if (!root.startsWith('/')) {
            root = '/' + root;
        }
        if (!root.endsWith('/')) {
            root += '/';
        }
        return root;
    }

    // Helper function to fetch product json from the target store using XHR to bypass app wrappers like UpCart
    function getTargetStoreProduct(handle) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const rootPath = getRootPath();
            xhr.open('GET', `${TARGET_STORE_DOMAIN}${rootPath}products/${handle}.js`, true);
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        try {
                            resolve(JSON.parse(xhr.responseText));
                        } catch (e) {
                            reject(e);
                        }
                    } else {
                        resolve(null); // Return null instead of rejecting so promise.all doesn't fail everything
                    }
                }
            };
            xhr.onerror = function () {
                resolve(null);
            };
            xhr.send();
        });
    }

    // Lookup the target variant ID using SKU
    async function getTargetVariantId(handle, sku) {
        const targetProduct = await getTargetStoreProduct(handle);
        if (!targetProduct || !targetProduct.variants) return null;

        const matchedVariant = targetProduct.variants.find(v => v.sku === sku);
        return matchedVariant ? matchedVariant.id : null;
    }

    // Process a list of cart items and redirect
    async function processAndRedirectCart(items) {
        const permalinkItems = [];

        // Process each item concurrently to speed up network requests
        const mappings = await Promise.all(items.map(async (item) => {
            if (!item.sku || !item.handle) return null;

            const targetId = await getTargetVariantId(item.handle, item.sku);
            if (targetId) {
                return `${targetId}:${item.quantity}`;
            } else {
                console.warn(`Could not map item ${item.handle} with SKU ${item.sku}`);
                return null;
            }
        }));

        // Filter out items that couldn't be mapped
        mappings.forEach(mapStr => {
            if (mapStr) permalinkItems.push(mapStr);
        });

        if (permalinkItems.length > 0) {
            const rootPath = getRootPath();
            const checkoutUrl = `${TARGET_STORE_DOMAIN}${rootPath}cart/${permalinkItems.join(',')}`;
            window.location.href = checkoutUrl;
        } else {
            console.error('No cart items could be mapped to the target store.');
            // Let default behavior fallback or show a gentle fallback if needed
        }
    }

    // Handle Checkout actions (from cart page or drawer)
    function handleCheckoutRedirect(e) {
        if (e && e.preventDefault) e.preventDefault();

        try {
            // Fetch current cart state using XHR to avoid UpCart fetch interceptors
            const xhr = new XMLHttpRequest();
            xhr.open('GET', '/cart.js', true);
            xhr.onreadystatechange = async function () {
                if (xhr.readyState === 4 && xhr.status === 200) {
                    try {
                        const cart = JSON.parse(xhr.responseText);
                        if (!cart || !cart.items || cart.items.length === 0) {
                            return; // Empty cart
                        }
                        await processAndRedirectCart(cart.items);
                    } catch (err) {
                        console.error('Error parsing cart data:', err);
                    }
                }
            };
            xhr.onerror = function () {
                console.error('Error fetching cart.js');
            };
            xhr.send();

        } catch (err) {
            console.error('Error during checkout redirection:', err);
        }
    }

    // Handle direct Add to Cart actions
    function handleAddToCartRedirect(e, form) {
        if (e && e.preventDefault) e.preventDefault();

        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1', 10);

        if (!variantId) return;

        try {
            // Submit to local cart first
            const addXhr = new XMLHttpRequest();
            addXhr.open('POST', '/cart/add.js', true);
            addXhr.setRequestHeader('Content-Type', 'application/json');
            addXhr.onreadystatechange = function () {
                if (addXhr.readyState === 4) {
                    // Now fetch the full cart
                    handleCheckoutRedirect(e);
                }
            };
            addXhr.send(JSON.stringify({ id: variantId, quantity: quantity }));
        } catch (err) {
            console.error('Error during add to cart redirection:', err);
        }
    }

    // Identify if the target node is a checkout button
    function isCheckoutTarget(target) {
        if (!target) return false;

        // Exact names matching
        if (target.name === 'checkout') return true;

        // Class matching for Upcart and others
        if (target.closest('.upcart-checkout-button')) return true;
        if (typeof target.className === 'string' && target.className.toLowerCase().includes('checkout-button')) return true;

        // Href matching
        const href = target.getAttribute('href') || target.href || '';
        if (typeof href === 'string') {
            if (href.includes('/checkout') && !href.includes('vkqux1-s0')) return true;
        }

        // Form matching
        const form = target.closest('form[action="/cart"], form[action="/checkout"], form.cart-form, form#cart-form, form[action*="/checkout"]');
        if (form && (target.type === 'submit' || target.name === 'checkout')) return true;

        return false;
    }

    // Attach event listeners
    function attachListeners() {
        // A MutationObserver guarantees we catch dynamically injected buttons like UpCart's drawer
        const observer = new MutationObserver(() => {
            const checkoutBtns = document.querySelectorAll('a[href="/checkout"], a[href*="/checkout"], a.upcart-checkout-button, button[name="checkout"], .upcart-checkout-button');
            checkoutBtns.forEach(btn => {
                if (!btn.dataset.cleaned) {
                    btn.dataset.cleaned = 'true';
                    // Strip the href attribute so native navigation is literally impossible
                    if (btn.tagName.toLowerCase() === 'a') {
                        btn.removeAttribute('href');
                        btn.style.cursor = 'pointer'; // keep it looking clickable
                    }
                }
            });
        });

        // Start observing the whole document for added nodes
        observer.observe(document.documentElement, { childList: true, subtree: true });

        // Aggressively kill ALL interaction events on checkout buttons in capture phase
        let isRedirecting = false;
        // Catch it early with mousedown before React 'click' even triggers
        const pointerEvents = ['click', 'mousedown', 'pointerdown', 'touchstart', 'touchend'];

        pointerEvents.forEach(evtType => {
            document.addEventListener(evtType, function (e) {
                const path = e.composedPath ? e.composedPath() : [e.target];
                let isCheckout = false;

                for (let i = 0; i < path.length; i++) {
                    const el = path[i];
                    if (el && el.nodeType === 1) { // Element node
                        if (
                            (el.tagName === 'A' && el.getAttribute('href') && typeof el.getAttribute('href') === 'string' && el.getAttribute('href').includes('/checkout') && !el.getAttribute('href').includes('vkqux1-s0')) ||
                            el.name === 'checkout' ||
                            (el.classList && (el.classList.contains('upcart-checkout-button') || el.classList.contains('upcart-checkout-button-container') || el.className.toString().indexOf('checkout-button') !== -1)) ||
                            (el.tagName === 'BUTTON' && el.name === 'checkout')
                        ) {
                            if (el.tagName === 'A' && el.getAttribute('href') === '/cart') continue;
                            isCheckout = true;
                            break;
                        }
                    }
                }

                if (isCheckout) {
                    // It is a checkout button! Stop all propagation immediately
                    e.preventDefault();
                    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
                    e.stopPropagation();

                    // Only initiate redirect on finalizing actions or mousedown
                    if (!isRedirecting && (evtType === 'click' || evtType === 'touchend' || evtType === 'mousedown')) {
                        isRedirecting = true;
                        handleCheckoutRedirect(e);

                        // Safety timeout in case something failed visually
                        setTimeout(() => { isRedirecting = false; }, 3000);
                    }
                }
            }, true); // Use capture phase to intercept before React or other listeners
        });

        // Also catch form submits directly
        document.addEventListener('submit', function (e) {
            const form = e.target;
            if (form && typeof form.getAttribute === 'function') {
                if (form.getAttribute('action') === '/cart' || form.getAttribute('action') === '/checkout' || form.id === 'cart-form' || form.classList.contains('cart-form')) {
                    const activeElement = document.activeElement;
                    if ((activeElement && activeElement.name === 'checkout') || (e.submitter && e.submitter.name === 'checkout') || (form.querySelector('button[name="checkout"]'))) {
                        e.preventDefault();
                        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
                        e.stopPropagation();

                        if (!isRedirecting) {
                            isRedirecting = true;
                            handleCheckoutRedirect(e);
                            setTimeout(() => { isRedirecting = false; }, 3000);
                        }
                    }
                }
            }
        }, true);
    }

    // Run on load
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', attachListeners);
    } else {
        attachListeners();
    }

})();
